import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Heart, ChevronRight, ChevronLeft, Leaf } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ETHNICITIES = [
  { value: 'white', label: 'White/Caucasian' },
  { value: 'black', label: 'Black/African American' },
  { value: 'asian', label: 'Asian' },
  { value: 'hispanic', label: 'Hispanic/Latino' },
  { value: 'middle_eastern', label: 'Middle Eastern' },
  { value: 'native_american', label: 'Native American' },
  { value: 'pacific_islander', label: 'Pacific Islander' },
  { value: 'mixed', label: 'Mixed/Multiracial' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

const RELATIONSHIP_GOALS = [
  { value: 'casual', label: 'Casual Dating' },
  { value: 'dating', label: 'Dating & Seeing Where It Goes' },
  { value: 'serious', label: 'Serious Relationship' },
  { value: 'marriage', label: 'Marriage-Minded' }
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    ethnicity: '',
    location_city: '',
    location_state: '',
    bio: '',
    looking_for: user?.gender === 'female' ? 'male' : 'female',
    relationship_goal: '',
    size_preference_min: 2,
    size_preference_max: 5,
    profile_photo_url: ''
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/onboarding`, formData);
      updateUser(response.data);
      
      // Seed initial data
      await axios.post(`${API_URL}/seed`);
      
      toast.success('Profile completed! Welcome to Twig.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-page">
      {/* Header */}
      <div className="border-b border-border">
        <div className="container-app py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-2xl font-serif font-semibold">Twig</span>
          </div>
          <p className="text-sm text-muted-foreground">Step {step} of {totalSteps}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="container-app pt-4">
        <Progress value={progress} className="h-2" />
      </div>

      {/* Content */}
      <div className="container-app py-12">
        <div className="max-w-xl mx-auto">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="animate-slideIn" data-testid="onboarding-step-1">
              <h1 className="text-3xl font-serif font-semibold mb-2">Tell us about yourself</h1>
              <p className="text-muted-foreground mb-8">This helps us find compatible matches for you.</p>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>Ethnicity</Label>
                  <Select value={formData.ethnicity} onValueChange={(v) => handleChange('ethnicity', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="ethnicity-select">
                      <SelectValue placeholder="Select your ethnicity" />
                    </SelectTrigger>
                    <SelectContent>
                      {ETHNICITIES.map((e) => (
                        <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      placeholder="Your city"
                      value={formData.location_city}
                      onChange={(e) => handleChange('location_city', e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select value={formData.location_state} onValueChange={(v) => handleChange('location_state', v)}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: What You're Looking For */}
          {step === 2 && (
            <div className="animate-slideIn" data-testid="onboarding-step-2">
              <h1 className="text-3xl font-serif font-semibold mb-2">What are you looking for?</h1>
              <p className="text-muted-foreground mb-8">Help us understand your relationship goals.</p>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>I'm interested in</Label>
                  <Select value={formData.looking_for} onValueChange={(v) => handleChange('looking_for', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="looking-for-select">
                      <SelectValue placeholder="Select who you're interested in" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Men</SelectItem>
                      <SelectItem value="female">Women</SelectItem>
                      <SelectItem value="non_binary">Non-binary</SelectItem>
                      <SelectItem value="other">Everyone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Relationship Goal</Label>
                  <Select value={formData.relationship_goal} onValueChange={(v) => handleChange('relationship_goal', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="relationship-goal-select">
                      <SelectValue placeholder="What are you looking for?" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONSHIP_GOALS.map((g) => (
                        <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {user?.gender === 'female' && (
                  <div className="space-y-3 p-4 rounded-xl bg-accent/50">
                    <Label>Size Preference (inches)</Label>
                    <p className="text-sm text-muted-foreground">
                      Twig is a non-fetishistic platform for cis and trans men with below-average size (2-4.9"). 
                      Our female members specifically prefer partners in this range.
                    </p>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Minimum</Label>
                        <Input
                          type="number"
                          min={2}
                          max={5}
                          step={0.5}
                          value={formData.size_preference_min}
                          onChange={(e) => handleChange('size_preference_min', parseFloat(e.target.value))}
                          className="h-10 rounded-lg"
                          data-testid="size-min-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Maximum</Label>
                        <Input
                          type="number"
                          min={2}
                          max={5}
                          step={0.5}
                          value={formData.size_preference_max}
                          onChange={(e) => handleChange('size_preference_max', parseFloat(e.target.value))}
                          className="h-10 rounded-lg"
                          data-testid="size-max-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Bio */}
          {step === 3 && (
            <div className="animate-slideIn" data-testid="onboarding-step-3">
              <h1 className="text-3xl font-serif font-semibold mb-2">Write your bio</h1>
              <p className="text-muted-foreground mb-8">Share a bit about yourself to help others get to know you.</p>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>About Me</Label>
                  <Textarea
                    placeholder="Share your interests, what makes you unique, and what you're looking for in a partner..."
                    value={formData.bio}
                    onChange={(e) => handleChange('bio', e.target.value)}
                    className="min-h-[200px] rounded-xl"
                    maxLength={500}
                    data-testid="bio-textarea"
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {formData.bio.length}/500 characters
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Photo */}
          {step === 4 && (
            <div className="animate-slideIn" data-testid="onboarding-step-4">
              <h1 className="text-3xl font-serif font-semibold mb-2">Add a profile photo</h1>
              <p className="text-muted-foreground mb-8">
                Your photo will be blurred until you match. Privacy is our priority.
              </p>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>Photo URL</Label>
                  <Input
                    placeholder="Paste a photo URL (we'll add uploads soon)"
                    value={formData.profile_photo_url}
                    onChange={(e) => handleChange('profile_photo_url', e.target.value)}
                    className="h-12 rounded-xl"
                    data-testid="photo-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    For now, paste a URL to your photo. Direct uploads coming soon!
                  </p>
                </div>

                {formData.profile_photo_url && (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-3">Preview (blurred for privacy)</p>
                    <div className="inline-block">
                      <img 
                        src={formData.profile_photo_url} 
                        alt="Profile preview"
                        className="w-48 h-48 object-cover rounded-2xl blur-lg"
                      />
                    </div>
                  </div>
                )}

                <div className="p-4 rounded-xl bg-accent/50">
                  <p className="text-sm">
                    <strong>Privacy Note:</strong> Your profile photo will only be fully visible to people you match with.
                    All other users will see a blurred version.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-12">
            {step > 1 ? (
              <Button 
                variant="outline" 
                onClick={prevStep}
                className="rounded-full px-6"
                data-testid="prev-step-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < totalSteps ? (
              <Button 
                onClick={nextStep}
                className="rounded-full px-6"
                data-testid="next-step-btn"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="rounded-full px-8"
                data-testid="complete-onboarding-btn"
              >
                {loading ? 'Saving...' : 'Complete Profile'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
